import React, { useContext, useState, useEffect } from 'react';
import { AppContext } from '../../App';
import { authAxios } from '../../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

export default function CreateSeasonWizard({ open, onClose, onSuccess, league }) {
  const { currentSeason, seasons, API } = useContext(AppContext);
  
  // Wizard steps
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  
  // Step 1: Season Basics
  const [seasonNumber, setSeasonNumber] = useState('');
  const [seasonTitle, setSeasonTitle] = useState('');
  const [episodeCount, setEpisodeCount] = useState('13');
  const [premiereDate, setPremiereDate] = useState('');
  const [airTime, setAirTime] = useState('20:00'); // Default 8:00 PM
  const [timezone, setTimezone] = useState('America/New_York'); // Default Eastern
  const [weeklyInterval, setWeeklyInterval] = useState('7'); // Default weekly (7 days)
  
  // Common US timezones
  const timezones = [
    { value: 'America/New_York', label: 'Eastern (ET)' },
    { value: 'America/Chicago', label: 'Central (CT)' },
    { value: 'America/Denver', label: 'Mountain (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
    { value: 'America/Anchorage', label: 'Alaska (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
    { value: 'UTC', label: 'UTC' },
  ];
  
  // Step 2: Scoring Rules Source
  const [scoringRulesOption, setScoringRulesOption] = useState('current'); // current, previous, default, scratch
  const [selectedPreviousSeason, setSelectedPreviousSeason] = useState('');
  
  // Step 3: Contestant List
  const [uploadFile, setUploadFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Import from default contestants
  const [contestantOption, setContestantOption] = useState('manual'); // manual, import, upload
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [availableDefaultContestants, setAvailableDefaultContestants] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  
  // Step 4: Add Players
  const [allUsers, setAllUsers] = useState([]);
  const [allInvites, setAllInvites] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Previous seasons for dropdown (all seasons except current)
  const previousSeasons = seasons.filter(s => s.id !== currentSeason?.id);
  
  // Set default scoring rules option based on current season (only on mount)
  useEffect(() => {
    if (currentSeason) {
      setScoringRulesOption('current');
    } else if (previousSeasons.length > 0) {
      setScoringRulesOption('previous');
    } else {
      setScoringRulesOption('default');
    }
  }, []); // Empty dependency array - only run once on mount
  
  // Load users and invites when wizard opens
  useEffect(() => {
    if (open) {
      loadUsersAndInvites();
      loadCountries();
    }
  }, [open]);
  
  const loadCountries = async () => {
    try {
      const response = await authAxios.get(`${API}/platform/countries/active`);
      setCountries(response.data || []);
    } catch (error) {
      console.error('Failed to load countries:', error);
    }
  };
  
  const checkDefaultContestantsAvailability = async () => {
    if (!seasonNumber || !selectedCountry) {
      setAvailableDefaultContestants(null);
      return;
    }
    
    try {
      setCheckingAvailability(true);
      const response = await authAxios.get(
        `${API}/platform/default-contestants/available?season_number=${seasonNumber}&country_id=${selectedCountry}`
      );
      setAvailableDefaultContestants(response.data);
    } catch (error) {
      console.error('Failed to check availability:', error);
      setAvailableDefaultContestants(null);
    } finally {
      setCheckingAvailability(false);
    }
  };
  
  // Check availability when season number or country changes
  useEffect(() => {
    if (contestantOption === 'import') {
      checkDefaultContestantsAvailability();
    }
  }, [seasonNumber, selectedCountry, contestantOption]);
  
  const loadUsersAndInvites = async () => {
    try {
      setLoadingUsers(true);
      
      // Load all users (active, deleted)
      const usersResponse = await authAxios.get(`${API}/users`);
      setAllUsers(usersResponse.data);
      
      // Load only pending invites
      const invitesResponse = await authAxios.get(`${API}/users/invites?status=pending`);
      setAllInvites(invitesResponse.data);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };
  
  const togglePlayerSelection = (playerId) => {
    if (selectedPlayers.includes(playerId)) {
      setSelectedPlayers(selectedPlayers.filter(id => id !== playerId));
    } else {
      setSelectedPlayers([...selectedPlayers, playerId]);
    }
  };
  
  const selectAllPlayers = () => {
    // Only include visible users and invites
    const allPlayerIds = [
      ...allUsers.map(u => u.id),
      ...allInvites.map(i => i.id)  // allInvites already filtered to pending
    ];
    setSelectedPlayers(allPlayerIds);
  };
  
  const deselectAllPlayers = () => {
    setSelectedPlayers([]);
  };
  
  const handleNext = () => {
    // Validation for each step
    if (currentStep === 1) {
      if (!seasonNumber) {
        toast.error('Please enter a season number');
        return;
      }
      if (!episodeCount || parseInt(episodeCount) < 1) {
        toast.error('Please enter a valid episode count');
        return;
      }
    }
    
    if (currentStep === 2) {
      if (scoringRulesOption === 'previous' && !selectedPreviousSeason) {
        toast.error('Please select a previous season');
        return;
      }
    }
    
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const downloadTemplate = () => {
    // Create CSV template with all fields
    const headers = ['Contestant Name', 'Tribe', 'Age', 'Hometown', 'Notes', 'Photo URL'];
    const sampleRow = ['John Doe', 'Red Tribe', '28', 'Los Angeles, CA', 'Season favorite', 'https://example.com/photo.jpg'];
    const csvContent = headers.join(',') + '\n' + sampleRow.join(',');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'contestant_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Template downloaded!');
  };
  
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadFile(file);
    
    // Parse and validate file
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await authAxios.post(
        `${API}/contestants/parse-upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setPreviewData(response.data);
      setValidationErrors(response.data.errors || []);
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`File parsed with ${response.data.errors.length} warning(s)`);
      } else {
        toast.success(`${response.data.valid_count} contestants ready to import`);
      }
    } catch (error) {
      console.error('Failed to parse file:', error);
      toast.error(error.response?.data?.detail || 'Failed to parse file');
      setUploadFile(null);
    } finally {
      setUploading(false);
    }
  };
  
  const handleCreate = async () => {
    try {
      setUploading(true);
      
      // Create season
      const seasonData = {
        league_id: league.id,
        number: parseInt(seasonNumber),
        title: seasonTitle || null,
        episode_count: parseInt(episodeCount),
        start_date: premiereDate || null,
        air_time: airTime || null,
        timezone: timezone || 'America/New_York',
        episode_interval_days: parseInt(weeklyInterval) || 7
      };
      
      console.log('Step 1: Creating season...');
      const seasonResponse = await authAxios.post(
        `${API}/seasons`,
        seasonData
      );
      
      const newSeasonId = seasonResponse.data.id;
      console.log('Step 1 SUCCESS: Season created with ID:', newSeasonId);
      
      // Auto-generate episode calendar if premiere date is set
      if (premiereDate) {
        console.log('Step 1.5: Generating episode calendar...');
        await authAxios.post(
          `${API}/seasons/${newSeasonId}/calendar/generate`,
          { 
            start_date: premiereDate,
            air_time: airTime,
            timezone: timezone,
            interval_days: parseInt(weeklyInterval) || 7
          }
        );
        console.log('Step 1.5 SUCCESS: Episode calendar generated');
      }
      
      // Copy scoring rules if selected
      if (scoringRulesOption === 'current' && currentSeason) {
        console.log('Step 2: Copying scoring rules from current season...');
        await authAxios.post(
          `${API}/seasons/${newSeasonId}/copy-scoring-rules`,
          { source_season_id: currentSeason.id }
        );
        console.log('Step 2 SUCCESS: Scoring rules copied');
      } else if (scoringRulesOption === 'previous' && selectedPreviousSeason) {
        console.log('Step 2: Copying scoring rules from previous season...');
        await authAxios.post(
          `${API}/seasons/${newSeasonId}/copy-scoring-rules`,
          { source_season_id: selectedPreviousSeason }
        );
        console.log('Step 2 SUCCESS: Scoring rules copied');
      } else {
        console.log('Step 2 SKIPPED: Using default rules');
      }
      // If 'default' or 'scratch', don't copy any rules 
      // (default rules are already created by backend during season creation)
      
      // Upload contestants if file provided OR import from defaults
      if (contestantOption === 'upload' && uploadFile && previewData) {
        console.log('Step 3: Uploading contestants from file...');
        const formData = new FormData();
        formData.append('file', uploadFile);
        
        await authAxios.post(
          `${API}/seasons/${newSeasonId}/contestants/bulk-upload`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        console.log('Step 3 SUCCESS: Contestants uploaded');
      } else if (contestantOption === 'import' && availableDefaultContestants?.available) {
        console.log('Step 3: Importing default contestants...');
        const importResult = await authAxios.post(
          `${API}/seasons/${newSeasonId}/import-default-contestants`,
          { 
            season_number: parseInt(seasonNumber),
            country_id: selectedCountry
          }
        );
        console.log('Step 3 SUCCESS:', importResult.data.message);
        if (importResult.data.skipped_count > 0) {
          toast.info(`Skipped ${importResult.data.skipped_count} duplicate contestants`);
        }
      } else {
        console.log('Step 3 SKIPPED: No contestants to import');
      }
      
      // Add players to season if selected
      if (selectedPlayers.length > 0) {
        console.log('Step 4: Adding', selectedPlayers.length, 'players to season...');
        for (const playerId of selectedPlayers) {
          await authAxios.post(
            `${API}/users/${playerId}/seasons`,
            { action: 'add', season_ids: [newSeasonId] }
          );
        }
        console.log('Step 4 SUCCESS: Players added to season');
      } else {
        console.log('Step 4 SKIPPED: No players selected');
      }
      
      console.log('ALL STEPS COMPLETE: Season creation successful!');
      toast.success('Season created successfully!');
      onSuccess();
      resetWizard();
    } catch (error) {
      console.error('Failed to create season:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error(error.response?.data?.detail || 'Failed to create season');
    } finally {
      setUploading(false);
    }
  };
  
  const resetWizard = () => {
    setCurrentStep(1);
    setSeasonNumber('');
    setSeasonTitle('');
    setEpisodeCount('13');
    setPremiereDate('');
    setAirTime('20:00');
    setTimezone('America/New_York');
    setWeeklyInterval('7');
    setScoringRulesOption(currentSeason ? 'current' : 'default');
    setSelectedPreviousSeason('');
    setUploadFile(null);
    setPreviewData(null);
    setValidationErrors([]);
    setSelectedPlayers([]);
    setContestantOption('manual');
    setSelectedCountry('');
    setAvailableDefaultContestants(null);
  };
  
  const handleClose = () => {
    resetWizard();
    onClose();
  };
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Create New Season - Step {currentStep} of {totalSteps}</DialogTitle>
          <div className="flex gap-2 mt-4">
            {[1, 2, 3, 4].map(step => (
              <div
                key={step}
                className={`h-2 flex-1 rounded ${
                  step <= currentStep ? 'bg-amber-600' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </DialogHeader>
        
        <div className="py-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Step 1: Season Basics */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Season Basics</h3>
              
              <div>
                <Label htmlFor="seasonNumber">Season Number *</Label>
                <Input
                  id="seasonNumber"
                  type="number"
                  value={seasonNumber}
                  onChange={(e) => setSeasonNumber(e.target.value)}
                  placeholder="e.g., 47"
                />
              </div>
              
              <div>
                <Label htmlFor="seasonTitle">Season Title (optional)</Label>
                <Input
                  id="seasonTitle"
                  value={seasonTitle}
                  onChange={(e) => setSeasonTitle(e.target.value)}
                  placeholder="e.g., New Era"
                />
              </div>
              
              <div>
                <Label htmlFor="episodeCount">Episode Count *</Label>
                <Input
                  id="episodeCount"
                  type="number"
                  value={episodeCount}
                  onChange={(e) => setEpisodeCount(e.target.value)}
                  placeholder="13"
                />
              </div>
              
              <div>
                <Label htmlFor="premiereDate">Premiere Date (optional)</Label>
                <Input
                  id="premiereDate"
                  type="date"
                  value={premiereDate}
                  onChange={(e) => setPremiereDate(e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1">Episode 1 air date - used to auto-generate episode calendar</p>
              </div>
              
              {/* Only show Air Time, Timezone, and Weekly Interval if Premiere Date is set */}
              {premiereDate && (
                <>
                  <div>
                    <Label htmlFor="airTime">Air Time</Label>
                    <Input
                      id="airTime"
                      type="time"
                      value={airTime}
                      onChange={(e) => setAirTime(e.target.value)}
                    />
                    <p className="text-xs text-slate-500 mt-1">Time episodes air (e.g., 8:00 PM = 20:00)</p>
                  </div>
                  
                  <div>
                    <Label htmlFor="timezone">Time Zone</Label>
                    <Select value={timezone} onValueChange={setTimezone}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select timezone" />
                      </SelectTrigger>
                      <SelectContent>
                        {timezones.map(tz => (
                          <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">Season timezone for notifications</p>
                  </div>
                  
                  <div>
                    <Label htmlFor="weeklyInterval">Weekly Interval (days)</Label>
                    <Select value={weeklyInterval} onValueChange={setWeeklyInterval}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select interval" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">Weekly (7 days)</SelectItem>
                        <SelectItem value="14">Bi-weekly (14 days)</SelectItem>
                        <SelectItem value="1">Daily (1 day)</SelectItem>
                        <SelectItem value="3">Every 3 days</SelectItem>
                        <SelectItem value="4">Every 4 days</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">Days between episodes</p>
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* Step 2: Scoring Rules Source */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Set Up Scoring Rules</h3>
              <p className="text-sm text-slate-600">
                Choose how to set up scoring rules for this season. Select one option below.
              </p>
              
              <div className="space-y-3">
                {currentSeason && (
                  <div 
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      scoringRulesOption === 'current' 
                        ? 'border-amber-500 bg-amber-50' 
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    onClick={() => setScoringRulesOption('current')}
                  >
                    <div className="font-semibold text-base">Keep Current Season Rules</div>
                    <div className="text-sm text-slate-600 mt-1">
                      Copy the scoring rules from Season {currentSeason.number}
                      {currentSeason.title ? `: ${currentSeason.title}` : ''}
                    </div>
                  </div>
                )}
                
                {previousSeasons.length > 0 && (
                  <div 
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      scoringRulesOption === 'previous' 
                        ? 'border-amber-500 bg-amber-50' 
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    onClick={(e) => {
                      // Don't trigger when clicking inside Select dropdown
                      if (!e.target.closest('[role="combobox"]') && !e.target.closest('[role="listbox"]')) {
                        setScoringRulesOption('previous');
                      }
                    }}
                  >
                    <div className="font-semibold text-base">Select from a Previous Season</div>
                    <div className="text-sm text-slate-600 mt-1 mb-3">
                      Choose scoring rules from any past season
                    </div>
                    
                    {scoringRulesOption === 'previous' && (
                      <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                        <Select value={selectedPreviousSeason} onValueChange={setSelectedPreviousSeason}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select previous season" />
                          </SelectTrigger>
                          <SelectContent>
                            {previousSeasons.map(season => (
                              <SelectItem key={season.id} value={season.id}>
                                Season {season.number}
                                {season.title ? `: ${season.title}` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}
                
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    scoringRulesOption === 'default' 
                      ? 'border-amber-500 bg-amber-50' 
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  onClick={() => setScoringRulesOption('default')}
                >
                  <div className="font-semibold text-base">Use System Default Rules</div>
                  <div className="text-sm text-slate-600 mt-1">
                    Start with the app's default scoring rules
                  </div>
                </div>
                
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    scoringRulesOption === 'scratch' 
                      ? 'border-amber-500 bg-amber-50' 
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  onClick={() => setScoringRulesOption('scratch')}
                >
                  <div className="font-semibold text-base">Start from Scratch</div>
                  <div className="text-sm text-slate-600 mt-1">
                    Begin with a blank set of rules and build your own from the ground up
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 3: Contestant List */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Contestant List (optional)</h3>
              <p className="text-sm text-slate-600">
                Import contestants from the platform library, upload your own list, or add them manually later.
              </p>
              
              {/* Option selector */}
              <div className="space-y-3">
                {/* Import from defaults option */}
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    contestantOption === 'import' 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  onClick={() => setContestantOption('import')}
                >
                  <div className="font-semibold text-base flex items-center gap-2">
                    <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Import from Platform Library
                  </div>
                  <div className="text-sm text-slate-600 mt-1">
                    Use the official contestant list for Season {seasonNumber || '[number]'}
                  </div>
                </div>
                
                {/* Show country selector when import is selected */}
                {contestantOption === 'import' && (
                  <div className="ml-6 p-4 border rounded-lg bg-slate-50 space-y-3">
                    <div>
                      <Label>Select Country</Label>
                      <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Choose a country..." />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map(country => (
                            <SelectItem key={country.id} value={country.id}>
                              {country.name} ({country.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Availability status */}
                    {checkingAvailability ? (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-400"></div>
                        Checking availability...
                      </div>
                    ) : availableDefaultContestants ? (
                      availableDefaultContestants.available ? (
                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <div className="flex items-center gap-2 text-emerald-800 font-medium">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {availableDefaultContestants.count} contestant(s) available!
                          </div>
                          <div className="text-sm text-emerald-700 mt-1">
                            Will be imported when you create the season
                          </div>
                          
                          {/* Preview list */}
                          {availableDefaultContestants.contestants && availableDefaultContestants.contestants.length > 0 && (
                            <div className="mt-3 max-h-40 overflow-y-auto">
                              <div className="grid grid-cols-2 gap-1 text-sm">
                                {availableDefaultContestants.contestants.slice(0, 10).map((c, idx) => (
                                  <div key={idx} className="text-emerald-800">{c.name}</div>
                                ))}
                                {availableDefaultContestants.contestants.length > 10 && (
                                  <div className="text-emerald-600 col-span-2">
                                    ...and {availableDefaultContestants.contestants.length - 10} more
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-center gap-2 text-amber-800">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            No contestants available for Season {seasonNumber} in this country
                          </div>
                          <div className="text-sm text-amber-700 mt-1">
                            Try a different country or upload your own list
                          </div>
                        </div>
                      )
                    ) : selectedCountry && seasonNumber ? (
                      <div className="text-sm text-slate-500">
                        Select a country to check available contestants
                      </div>
                    ) : null}
                  </div>
                )}
                
                {/* Upload file option */}
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    contestantOption === 'upload' 
                      ? 'border-amber-500 bg-amber-50' 
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  onClick={() => setContestantOption('upload')}
                >
                  <div className="font-semibold text-base flex items-center gap-2">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Upload Custom List
                  </div>
                  <div className="text-sm text-slate-600 mt-1">
                    Upload a CSV or Excel file with your own contestant data
                  </div>
                </div>
                
                {/* Show file upload controls when upload is selected */}
                {contestantOption === 'upload' && (
                  <div className="ml-6 p-4 border rounded-lg bg-slate-50">
                    <div className="flex gap-3 mb-3">
                      <Button variant="outline" size="sm" onClick={downloadTemplate}>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download Template
                      </Button>
                      
                      <div>
                        <input
                          type="file"
                          id="contestantFile"
                          accept=".xlsx,.csv"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('contestantFile').click()}
                          disabled={uploading}
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          {uploading ? 'Processing...' : 'Select File'}
                        </Button>
                      </div>
                    </div>
                    
                    {uploadFile && (
                      <div className="p-3 bg-white border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">{uploadFile.name}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setUploadFile(null);
                              setPreviewData(null);
                              setValidationErrors([]);
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                        
                        {previewData && (
                          <>
                            <div className="text-sm text-slate-600 mb-2">
                              {previewData.valid_count} contestant(s) will be created
                            </div>
                            
                            {validationErrors.length > 0 && (
                              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 mb-2">
                                {validationErrors.length} warning(s)
                              </div>
                            )}
                            
                            {previewData.preview && previewData.preview.length > 0 && (
                              <div className="border rounded overflow-hidden">
                                <div className="overflow-x-auto max-h-40">
                                  <table className="w-full text-xs">
                                    <thead className="bg-slate-50">
                                      <tr>
                                        <th className="px-2 py-1 text-left">Name</th>
                                        <th className="px-2 py-1 text-left">Tribe</th>
                                        <th className="px-2 py-1 text-left">Age</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {previewData.preview.slice(0, 5).map((row, idx) => (
                                        <tr key={idx} className="border-t">
                                          <td className="px-2 py-1">{row.full_name}</td>
                                          <td className="px-2 py-1">{row.tribe || '-'}</td>
                                          <td className="px-2 py-1">{row.age || '-'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Manual/Skip option */}
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    contestantOption === 'manual' 
                      ? 'border-slate-500 bg-slate-50' 
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  onClick={() => setContestantOption('manual')}
                >
                  <div className="font-semibold text-base flex items-center gap-2">
                    <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Add Manually Later
                  </div>
                  <div className="text-sm text-slate-600 mt-1">
                    Skip this step and add contestants through the Contestants page
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 4: Add Players */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Add Players to Season</h3>
              <p className="text-sm text-slate-600">
                Select players to add to Season {seasonNumber}. You can add active users, deleted users, or pending invites.
              </p>
              
              {loadingUsers ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
                  <p className="mt-4 text-slate-600">Loading players...</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-sm text-slate-600">
                      {selectedPlayers.length} player(s) selected
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={selectAllPlayers}>
                        Select All
                      </Button>
                      <Button size="sm" variant="outline" onClick={deselectAllPlayers}>
                        Deselect All
                      </Button>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left w-12">
                            <input
                              type="checkbox"
                              checked={selectedPlayers.length === allUsers.length + allInvites.length && (allUsers.length + allInvites.length) > 0}
                              onChange={(e) => e.target.checked ? selectAllPlayers() : deselectAllPlayers()}
                              className="rounded"
                            />
                          </th>
                          <th className="px-3 py-2 text-left">Name</th>
                          <th className="px-3 py-2 text-left">Email</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-left">Previous Seasons</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Regular Users */}
                        {allUsers.map(user => (
                          <tr 
                            key={user.id} 
                            className={`border-t hover:bg-slate-50 cursor-pointer ${
                              selectedPlayers.includes(user.id) ? 'bg-amber-50' : ''
                            }`}
                            onClick={() => togglePlayerSelection(user.id)}
                          >
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={selectedPlayers.includes(user.id)}
                                onChange={() => togglePlayerSelection(user.id)}
                                className="rounded"
                              />
                            </td>
                            <td className="px-3 py-2 font-medium">{user.name}</td>
                            <td className="px-3 py-2 text-slate-600">{user.email}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                user.status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                                user.status === 'deleted' ? 'bg-red-100 text-red-800' :
                                'bg-slate-100 text-slate-800'
                              }`}>
                                {user.status}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-slate-600">
                              {user.season_ids && user.season_ids.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {user.season_ids.map(seasonId => {
                                    const season = seasons.find(s => s.id === seasonId);
                                    return season ? (
                                      <span key={seasonId} className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-xs">
                                        S{season.number}
                                      </span>
                                    ) : null;
                                  })}
                                </div>
                              ) : (
                                <span className="text-slate-400 text-xs">No seasons</span>
                              )}
                            </td>
                          </tr>
                        ))}
                        
                        {/* Invites */}
                        {allInvites.map(invite => (
                          <tr 
                            key={invite.id} 
                            className={`border-t hover:bg-slate-50 cursor-pointer ${
                              selectedPlayers.includes(invite.id) ? 'bg-amber-50' : ''
                            }`}
                            onClick={() => togglePlayerSelection(invite.id)}
                          >
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={selectedPlayers.includes(invite.id)}
                                onChange={() => togglePlayerSelection(invite.id)}
                                className="rounded"
                              />
                            </td>
                            <td className="px-3 py-2 font-medium text-slate-600 italic">{invite.name || invite.email}</td>
                            <td className="px-3 py-2 text-slate-600">{invite.email}</td>
                            <td className="px-3 py-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                Invited
                              </span>
                            </td>
                            <td className="px-3 py-2 text-slate-600">
                              {invite.season_ids && invite.season_ids.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {invite.season_ids.map(seasonId => {
                                    const season = seasons.find(s => s.id === seasonId);
                                    return season ? (
                                      <span key={seasonId} className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-xs">
                                        S{season.number}
                                      </span>
                                    ) : null;
                                  })}
                                </div>
                              ) : (
                                <span className="text-slate-400 text-xs">No seasons</span>
                              )}
                            </td>
                          </tr>
                        ))}
                        
                        {allUsers.length === 0 && allInvites.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-3 py-8 text-center text-slate-600">
                              No users or invites found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      💡 You can skip this step and add players later through the Admin Players page.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        
        <DialogFooter>
          <div className="flex justify-between w-full">
            <Button
              variant="outline"
              onClick={currentStep === 1 ? handleClose : handleBack}
            >
              {currentStep === 1 ? 'Cancel' : 'Back'}
            </Button>
            
            <div className="flex gap-2">
              {currentStep < totalSteps && (
                <Button onClick={handleNext}>
                  Next
                </Button>
              )}
              
              {currentStep === totalSteps && (
                <Button onClick={handleCreate} disabled={uploading}>
                  {uploading ? 'Creating...' : 'Create Season'}
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
